import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  ServiceHero,
  AnimatedBreadcrumb,
  ProcessStepper,
  FeatureRow,
  UseCasesAccordion,
  StickyBookBar,
} from '../components/services';
import {
  TestimonialsSection,
  FAQSection,
  CTASection,
  GuaranteeSection,
} from '../components/landing';

// ============================================
// Service Icon
// ============================================
const PartnershipIcon: React.FC = () => (
  <svg className="w-12 h-12 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

// ============================================
// Check Icon
// ============================================
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
  </svg>
);

// ============================================
// What This Is Section
// ============================================
const WhatThisIsSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const cards = [
    {
      label: "What this is",
      title: "Not a one-off project. A continuous improvement engine.",
      body: "Most AI agencies build something, hand it over, and disappear. We stay. We learn your operations, map your workflows, identify where time and money are leaking — and then we build the automations that fix it. Month after month."
    },
    {
      label: "What you get",
      title: "A dedicated AI team without the hiring overhead",
      body: "You get ongoing access to a team that understands your systems deeply and proactively finds the next opportunity — instead of waiting to be asked."
    },
    {
      label: "What makes it different",
      title: "We audit first. We build only what actually matters.",
      body: "No scattershot automation. Every system we build is justified by a clear audit finding — with measurable time or cost savings estimated before we touch a line of code."
    }
  ];

  return (
    <section ref={ref} className="py-16 sm:py-20 md:py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30 pointer-events-none" />

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[150px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)' }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
          x: [0, 30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-20 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)' }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.2, 0.4, 0.2],
          x: [0, -20, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [1, 0.6, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">The Partnership Model</span>
          </div>
        </motion.div>

        <div className="space-y-6">
          {/* Full-width card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
            className="relative group"
          >
            {/* Card glow effect on hover */}
            <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-blue-500/20 via-violet-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500" />

            <div className="relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8 group-hover:border-blue-500/30 transition-all duration-500">
              {/* Inner gradient on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/30 group-hover:border-blue-500/30 transition-colors duration-300">
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-blue-400"
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider group-hover:text-zinc-300 transition-colors duration-300">{cards[0].label}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">{cards[0].title}</h3>
                <p className="text-zinc-400 leading-relaxed max-w-4xl">{cards[0].body}</p>
              </div>
            </div>
          </motion.div>

          {/* Two cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {cards.slice(1).map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
                className="relative group"
              >
                {/* Card glow effect on hover */}
                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-blue-500/20 via-violet-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500" />

                <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8 group-hover:border-blue-500/30 transition-all duration-500">
                  {/* Inner gradient on hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/30 group-hover:border-blue-500/30 transition-colors duration-300">
                      <motion.span
                        className="w-1.5 h-1.5 rounded-full bg-blue-400"
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                      />
                      <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider group-hover:text-zinc-300 transition-colors duration-300">{card.label}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">{card.title}</h3>
                    <p className="text-zinc-400 leading-relaxed">{card.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Deliverables Section
// ============================================
const DeliverablesSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const deliverables = [
    { name: 'Full operations map', description: 'A documented view of every major workflow across your business, with bottlenecks and manual tasks highlighted.', icon: 'map' },
    { name: 'Prioritized automation roadmap', description: 'Every opportunity ranked by ROI, complexity, and strategic value — so you know exactly what gets built and when.', icon: 'clipboard' },
    { name: 'AI agents & automations', description: 'Autonomous agents that handle repetitive decision-making, data processing, and cross-system coordination.', icon: 'cpu' },
    { name: 'Internal tools & dashboards', description: 'Custom-built interfaces your team actually uses — replacing spreadsheets and manual processes with purpose-built tools.', icon: 'wrench' },
    { name: 'Data pipelines & reporting', description: 'Clean, automated data flows that connect your tools and surface the metrics your leadership needs — without manual effort.', icon: 'link' },
    { name: 'Monthly performance reports', description: 'Clear reporting on what\'s running, what it\'s saving, and what\'s coming next — so leadership always has full visibility.', icon: 'chart' },
  ];

  const icons: Record<string, React.FC<{ className?: string }>> = {
    map: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" /></svg>,
    clipboard: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>,
    cpu: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z" /></svg>,
    wrench: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" /></svg>,
    link: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>,
    chart: ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>,
  };

  return (
    <section ref={ref} className="py-16 sm:py-20 md:py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30 pointer-events-none" />

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-blue-400/30"
          style={{
            left: `${15 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 4 + i,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Decorative gradient orbs */}
      <motion.div
        className="absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full blur-[150px] pointer-events-none"
        style={{ background: 'rgba(59, 130, 246, 0.08)' }}
        animate={{
          x: [0, -30, 0],
          opacity: [0.15, 0.25, 0.15],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-1/4 left-0 w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(139, 92, 246, 0.06)' }}
        animate={{
          x: [0, 20, 0],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [1, 0.6, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">What's Included</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">Everything You Get</h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">A complete AI transformation system — not a pile of disconnected deliverables.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deliverables.map((item, index) => {
            const IconComponent = icons[item.icon];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 + index * 0.08, duration: 0.5, ease: [0.215, 0.61, 0.355, 1] }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group relative"
              >
                {/* Hover glow effect */}
                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-violet-500/0 opacity-0 group-hover:opacity-100 blur-sm transition-all duration-500" />

                <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-6 group-hover:border-blue-500/30 transition-all duration-500">
                  {/* Inner gradient on hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative">
                    {/* Icon container with animation */}
                    <motion.div
                      className="w-12 h-12 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center mb-5 group-hover:border-blue-500/30 group-hover:bg-blue-500/10 transition-all duration-300"
                      whileHover={{ rotate: [0, -5, 5, 0], transition: { duration: 0.5 } }}
                    >
                      <IconComponent className="w-6 h-6 text-zinc-400 group-hover:text-blue-400 transition-colors duration-300" />
                    </motion.div>

                    <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-blue-50 transition-colors duration-300">{item.name}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{item.description}</p>

                    {/* Decorative corner accent */}
                    <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl pointer-events-none">
                      <div className="absolute top-0 right-0 w-[1px] h-8 bg-gradient-to-b from-blue-500/0 via-blue-500/20 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute top-0 right-0 w-8 h-[1px] bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================
// Fit Section
// ============================================
const FitCheckSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const fitItems = [
    "You run a team of 50–500 people with real operational complexity",
    "You know AI could help but don't know where to start",
    "You've tried point solutions and want something systemic",
    "You want a partner that takes ownership — not a vendor that needs hand-holding",
    "You think in terms of long-term ROI, not cheap quick fixes"
  ];

  const notFitItems = [
    "You want a one-off project with no ongoing relationship",
    "You're looking for the cheapest possible option",
    "Your leadership isn't bought in on AI transformation",
    "You need something shipped in a week"
  ];

  return (
    <section ref={ref} className="py-16 sm:py-20 md:py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-900/20 to-transparent pointer-events-none" />

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Decorative gradients */}
      <motion.div
        className="absolute top-1/3 left-0 w-[350px] h-[350px] rounded-full blur-[150px] pointer-events-none"
        style={{ background: 'rgba(16, 185, 129, 0.06)' }}
        animate={{
          opacity: [0.1, 0.2, 0.1],
          scale: [1, 1.1, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-1/3 right-0 w-[300px] h-[300px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(113, 113, 122, 0.05)' }}
        animate={{
          opacity: [0.1, 0.15, 0.1],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [1, 0.6, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Is This Right For You?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">Let's make sure we're a good fit</h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">This partnership works best for specific types of companies.</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Fit column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
            className="relative group"
          >
            {/* Badge */}
            <div className="absolute -top-3 left-6 z-10">
              <motion.div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <CheckIcon className="w-3.5 h-3.5 text-emerald-400" />
                </motion.div>
                <span className="text-emerald-400 text-xs font-medium">Good Fit</span>
              </motion.div>
            </div>

            {/* Card glow on hover */}
            <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500" />

            <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-emerald-500/20 p-8 group-hover:border-emerald-500/30 transition-all duration-500">
              {/* Inner gradient */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative">
                <h3 className="text-xl font-bold text-white mb-6">This is for you if</h3>
                <ul className="space-y-4">
                  {fitItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.3 + index * 0.1, duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
                      className="flex items-start gap-3 group/item"
                    >
                      <motion.div
                        className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover/item:bg-emerald-500/20 group-hover/item:border-emerald-500/40 transition-all duration-300"
                        whileHover={{ scale: 1.1 }}
                      >
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                      </motion.div>
                      <span className="text-zinc-300 leading-relaxed group-hover/item:text-zinc-200 transition-colors duration-300">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Not fit column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
            className="relative group"
          >
            {/* Badge */}
            <div className="absolute -top-3 left-6 z-10">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
                <MinusIcon className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-zinc-400 text-xs font-medium">Not For Everyone</span>
              </div>
            </div>

            <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8 group-hover:border-zinc-700/50 transition-all duration-500">
              <div className="relative">
                <h3 className="text-xl font-bold text-white mb-6">This is not for you if</h3>
                <ul className="space-y-4">
                  {notFitItems.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
                      className="flex items-start gap-3 group/item"
                    >
                      <div className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center group-hover/item:border-zinc-600/50 transition-all duration-300">
                        <MinusIcon className="w-3 h-3 text-zinc-500" />
                      </div>
                      <span className="text-zinc-400 leading-relaxed">{item}</span>
                    </motion.li>
                  ))}
                </ul>
                <motion.div
                  className="mt-8 pt-6 border-t border-zinc-800/50"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.8 }}
                >
                  <p className="text-zinc-500 text-sm leading-relaxed">No judgment — we just want to be upfront about where this works best.</p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// Main Page Component
// ============================================
export const AIAuditPartnershipPage: React.FC = () => {
  return (
    <main className="bg-[#0a0a0a]">
      {/* Sticky Book Now Bar */}
      <StickyBookBar
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        guarantee="Quarterly re-audit"
        accent="blue"
      />

      {/* Breadcrumb */}
      <AnimatedBreadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Services', href: '/#services' },
          { label: 'AI Audit Partnership' },
        ]}
        accent="blue"
      />

      {/* Hero Section */}
      <ServiceHero
        eyebrow="Flagship Service"
        subtitle="Long-Term AI Transformation"
        title="We embed in your business. Find waste. Build fixes. Keep going."
        description="The AI Audit Partnership is a long-term engagement where we systematically identify every workflow that can be automated — and then build the systems to do it."
        ctaText="Start the conversation"
        ctaHref="/contact"
        metrics={[
          { value: '50-500', label: 'Employee Companies' },
          { value: '30%+', label: 'Avg. Cost Reduction' },
          { value: 'Ongoing', label: 'Partnership Model' },
        ]}
        accent="blue"
        icon={<PartnershipIcon />}
      />

      {/* What This Is Section */}
      <WhatThisIsSection />

      {/* Process Section */}
      <section className="py-12 sm:py-16 md:py-20 border-t border-zinc-800/50 relative overflow-hidden">
        {/* Background elements */}
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-[150px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(59, 130, 246, 0.08) 0%, transparent 70%)' }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-10 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-blue-400"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [1, 0.6, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">How It Works</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              A structured partnership, not an open-ended retainer
            </h2>
            <p className="text-zinc-400">Every engagement follows the same proven sequence.</p>
          </motion.div>
        </div>

        <ProcessStepper
          accent="blue"
          autoAdvance={true}
          autoAdvanceInterval={5000}
          steps={[
            {
              title: 'Deep-dive operations audit',
              duration: 'Weeks 1-3',
              description: "We spend the first 2–3 weeks embedded in your business. We interview department leads, map every major workflow, and document where your team is spending time on work that could be automated.",
            },
            {
              title: 'Opportunity roadmap delivery',
              duration: 'Week 4',
              description: "We deliver a prioritized automation roadmap — ranked by ROI, effort, and strategic impact. You'll see exactly what we plan to build, why, and what outcome to expect from each system.",
            },
            {
              title: 'Monthly build cycles',
              duration: 'Ongoing',
              description: "We work through the roadmap in monthly sprints — designing, building, and deploying AI agents, workflow automations, internal tools, and data pipelines. You review before anything goes live.",
            },
            {
              title: 'Ongoing audit & expansion',
              duration: 'Quarterly',
              description: "Every quarter we re-audit. Your business changes — new processes emerge, new tools get adopted, new inefficiencies appear. We stay ahead of it, continuously surfacing the next opportunity to build.",
            },
          ]}
        />
      </section>

      {/* Deliverables Section */}
      <DeliverablesSection />

      {/* Feature Rows - Pain Points */}
      <section className="py-12 sm:py-16 md:py-20 border-t border-zinc-800/50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '64px 64px',
            }}
          />
        </div>

        <motion.div
          className="absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full blur-[150px] pointer-events-none"
          style={{ background: 'rgba(59, 130, 246, 0.05)' }}
          animate={{
            opacity: [0.1, 0.2, 0.1],
            x: [0, -20, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 left-0 w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none"
          style={{ background: 'rgba(139, 92, 246, 0.05)' }}
          animate={{
            opacity: [0.1, 0.15, 0.1],
            x: [0, 15, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
            className="text-center mb-12 md:mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm">
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-blue-400"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [1, 0.6, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">Why This Model</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Companies Choose This Model
            </h2>
            <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
              Traditional consulting leaves you with a deck. One-off projects leave you with a tool. We leave you with systems that compound.
            </p>
          </motion.div>

          <FeatureRow
            number="01"
            title="No more one-off projects that go stale"
            description="Every AI project you've done before eventually drifted. The model changed, the integration broke, or the team that understood it left. With an ongoing partnership, we maintain and evolve what we build."
            features={[
              "Continuous monitoring and maintenance",
              "Proactive updates as your business changes",
              "No knowledge loss when team members leave",
            ]}
            imagePosition="right"
            accent="blue"
          />

          <FeatureRow
            number="02"
            title="Compounding returns, not diminishing ones"
            description="Most AI projects plateau. We build on each cycle — connecting systems, sharing learnings, and finding efficiencies that only emerge once you've mapped the whole operation."
            features={[
              "Each build informs the next opportunity",
              "Cross-system integrations create multiplier effects",
              "Institutional knowledge accumulates over time",
            ]}
            imagePosition="left"
            accent="blue"
          />

          <FeatureRow
            number="03"
            title="A team that actually knows your business"
            description="By month three, we know your operations better than most of your employees. That depth is impossible to replicate with project-based vendors who start from scratch every time."
            features={[
              "Deep understanding of your workflows",
              "Relationships with key stakeholders",
              "Context that accelerates every decision",
            ]}
            imagePosition="right"
            accent="blue"
          />
        </div>
      </section>


      {/* Use Cases */}
      <UseCasesAccordion
        title="What We Typically Build"
        accent="blue"
        cases={[
          {
            title: 'AI Agents for Customer Operations',
            description: 'Autonomous agents that handle customer inquiries, route tickets, process requests, and escalate only when necessary. These agents learn from your existing data and improve over time.',
            result: 'Avg. 60% ticket deflection',
          },
          {
            title: 'Internal Workflow Automation',
            description: 'End-to-end automation of repetitive internal processes — approvals, data entry, report generation, cross-system syncing. We build what your team actually needs, not generic solutions.',
            result: 'Avg. 15 hrs/week saved per team',
          },
          {
            title: 'Custom Dashboards & Tools',
            description: 'Purpose-built interfaces that replace the spreadsheets and manual processes your team has been using. Designed for how your team actually works, not how a vendor thinks they should.',
            result: 'Avg. 40% faster decision-making',
          },
          {
            title: 'Data Pipeline & Reporting',
            description: 'Clean, automated data flows that connect your tools and surface the metrics leadership needs. No more manual exports, no more stale numbers, no more "I\'ll get back to you on that."',
            result: 'Real-time reporting across all tools',
          },
          {
            title: 'Document & Content Processing',
            description: 'AI-powered extraction, classification, and processing of documents — contracts, invoices, applications, whatever your business handles. Structured data out, manual work eliminated.',
            result: 'Avg. 80% processing time reduction',
          },
        ]}
      />

      {/* Fit Section */}
      <FitCheckSection />

      {/* Testimonials */}
      <TestimonialsSection
        headline="What Our Partners Say"
        testimonials={[
          {
            quote: "We'd been burned by AI projects before. The difference here is they stayed. Six months in, they know our operations better than some of our managers do.",
            author: 'Sarah Chen',
            role: 'COO',
            company: 'E-commerce Operations, 180 employees',
          },
          {
            quote: "The quarterly re-audits are where the magic happens. Every three months we find another 2-3 processes that should have been automated ages ago.",
            author: 'Michael Torres',
            role: 'VP Operations',
            company: 'Logistics Company, 320 employees',
          },
          {
            quote: "It's not a vendor relationship anymore. They're part of the team. And the compounding effect is real — each month builds on the last.",
            author: 'Jennifer Park',
            role: 'CTO',
            company: 'B2B SaaS, 95 employees',
          },
        ]}
        metrics={[
          { value: '30%+', label: 'Avg. cost reduction' },
          { value: '12', label: 'Months avg. engagement' },
          { value: '94%', label: 'Partner retention' },
        ]}
      />

      {/* FAQ Section */}
      <FAQSection
        headline="Common Questions"
        faqs={[
          {
            question: "How is this different from hiring an AI consultant?",
            answer: "Consultants give you a strategy deck. We give you working systems. And we stay to maintain and expand them. You get the outcome without the hiring overhead or the knowledge drain when the project ends."
          },
          {
            question: "What's the minimum commitment?",
            answer: "We start with a 3-month initial engagement. That gives us enough time to complete the deep-dive audit, deliver the roadmap, and execute the first build cycle. Most partners continue past that — our average engagement is 12+ months."
          },
          {
            question: "How do you price the partnership?",
            answer: "Pricing depends on your company size, the scope of what we're building, and the pace you want to move. We scope it together after a free strategy session so you know exactly what you're signing up for — no surprise invoices, no scope creep charges."
          },
          {
            question: "What if we want to bring the work in-house eventually?",
            answer: "That's the goal for some partners, and we support it. We document everything we build, train your team, and can transition ownership over time. You're never locked in."
          },
          {
            question: "How many clients do you take on?",
            answer: "We keep our partnership roster small — typically 8-10 active engagements at any time. This lets us go deep with each partner rather than spreading thin across dozens of clients."
          },
        ]}
      />

      {/* Guarantee Section */}
      <GuaranteeSection
        headline="Our Commitment"
        guarantee="Every quarter, we identify measurable opportunities. If we can't find ways to save you time or money, we'll tell you — and you can walk away."
        details={[
          "Transparent reporting on everything we build and the impact it creates",
          "Quarterly roadmap reviews where we re-prioritize based on what we've learned",
          "No long-term lock-in — you can end the partnership at any quarterly checkpoint",
        ]}
        riskStatement="Your commitment: scoped to your business"
        upsideStatement="Our commitment: continuous improvement that compounds"
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
      />

      {/* Final CTA */}
      <CTASection
        headline="Let's see what's possible in your business."
        subheadline="Book a 30-minute call. We'll map out where the biggest opportunities are and whether the Audit Partnership is the right fit."
        ctaText="Book a free discovery call"
        ctaHref="/contact"
        secondaryText="30-min call. No obligation. Talk with founders."
      />
    </main>
  );
};

export default AIAuditPartnershipPage;
